import {
  type AskPunttSearchProgress,
  type AskPunttStructuredContent,
} from "@mg/schemas/src/christo/askPuntt";

/**
 * Mock data for Ask Puntt feature development
 */

// Workflow shortcut cards shown on the home page
export const WORKFLOW_CARDS = [
  {
    id: "artwork_review",
    title: "Artwork Review",
    description: "Compare artwork against label sheets, spec sheets, and guidelines",
    icon: "🎨",
    workflowType: "compare" as const,
  },
  {
    id: "brief_comparison",
    title: "Brief Comparison",
    description: "Compare deliverables against briefs, checklists, and requirements",
    icon: "📑",
    workflowType: "compare" as const,
  },
  {
    id: "compliance_review",
    title: "Compliance Review",
    description: "Risk assessment with citations, alternatives, time saved",
    icon: "✅",
    workflowType: "compliance_review" as const,
  },
  {
    id: "claim_generation",
    title: "Claim Generation",
    description: "5-10 compliant claim options by risk level",
    icon: "💡",
    workflowType: "claim_generation" as const,
  },
] as const;

// Mock search progress data (simulates the progressive search UI)
export const MOCK_SEARCH_SOURCES: AskPunttSearchProgress[] = [
  {
    source: "Puntt Regulatory Database",
    status: "done",
    resultCount: 12,
    results: [
      {
        title: "Dairy Market Standards",
        url: "eur-lex.europa.eu/eli/reg/2013/1308...",
      },
      {
        title: "Food: Probiotic Claims - ASA | CAP",
        url: "www.asa.org.uk",
      },
      {
        title: "Nutrition & Health Claims Regulation",
        url: "eur-lex.europa.eu/eli/reg/2006/192...",
      },
      {
        title:
          "Written questions and answers - Written questions, answers, and stateme...",
        url: "questions-statements.parliament.uk",
      },
    ],
  },
  {
    source: "Danone's Claims Data",
    status: "done",
    resultCount: 4,
    results: [
      {
        title: 'Approved claims, Activia: "With active probiotics"',
        url: "danone.sharepoint.com/:f:/r/sites/...",
      },
      {
        title: "Dairy Actimel Simple DComm Assets Risk Assessment",
        url: "danone.sharepoint.com/:f:/r/sites/...",
      },
      {
        title: "Compliance Database - Dairy & Yogurt",
        url: "danone.sharepoint.com/:f:/r/sites/...",
      },
    ],
  },
  {
    source: "Wrike",
    status: "done",
    resultCount: 24,
    results: [
      {
        title:
          "2120671 - Activia - Asda - Activia Gut Health Challenge - Asda : ACT-24291325",
        url: "https://www.wrike.com/workspace...",
      },
      {
        title:
          "2033947 - Activia - Morrisons - Activia Gut Health Challenge - Morrisons : ACT-30590925",
        url: "https://www.wrike.com/workspace...",
      },
      {
        title:
          "1932413 - IE - Activia Kefir Launch Materials - Activia - Digital Display;Print",
        url: "https://www.wrike.com/workspace...",
      },
      {
        title:
          "2055004 - Actimel - Actimel PMP KV - Booker;Bestway;BRP;Bidfood",
        url: "https://www.wrike.com/workspace...",
      },
    ],
  },
];

// Mock structured response matching the Figma compliance review design
export const MOCK_STRUCTURED_RESPONSE: AskPunttStructuredContent = {
  riskLevel: "medium",
  confidence: "high",
  shortAnswer:
    '"with active probiotics" is a non-compliant health claim. The word "probiotic" is the primary problem, and "active" makes it worse by emphasizing beneficial function.',
  regulatoryAnalysis: `**Primary issue: Implied Health Claim**

Regulation (EC) No 1924/2006 (Nutrition and Health Claims Regulation) prohibits unauthorized health claims:

- Article 10(1): Health claims require authorization and inclusion in the EU Register of permitted claims
- "Active probiotics" implies functional benefit (gut health, immune support, digestive function)
- The word "active" specifically suggests biological activity/health effect

**EU Register Check**

I searched the EU Register of Nutrition and Health Claims for authorized probiotic claims:

- Result: NO general "probiotic" or "active probiotic" claims are authorized
- Status: All yogurt/live culture health claims under evaluation or rejected since 2012 EFSA opinions
- Key finding: Even strain-specific probiotic claims (L. casei, Bifidobacterium) have been rejected for insufficient evidence`,
  alternatives: [
    {
      riskLevel: "low",
      claim: "Contains active probiotics",
      why: "Factual statement, no health claim",
      precedent: "Danone Activia (UK), Yakult (EU)",
      status: "Factual statement, no health implication",
      approvalPath: "Clear - describes product composition without claims",
      tradeOff: "Less impactful but no substantiation needed",
    },
    {
      riskLevel: "medium",
      claim: "May contribute to gut health*",
      why: "Qualified claim with disclaimer",
      precedent: "23 similar products in UK",
      status: "Compositional information only",
      approvalPath: "Immediate use",
      tradeOff: "Requires existing clinical data",
    },
  ],
  sources: [
    { label: "Puntt Regulatory Database", type: "regulatory" },
    { label: "Danone Sharepoint", type: "internal" },
    { label: "EUR Lex", url: "eur-lex.europa.eu", type: "regulatory" },
    { label: "ASA", url: "www.asa.org.uk", type: "regulatory" },
  ],
  timeSaved: {
    summary: "2-3 Weeks",
    breakdown: [
      { week: "Week 1", description: "Regulatory flags health claim" },
      { week: "Week 2", description: "Request for substantiation docs" },
      { week: "Week 3", description: "Iteration with legal team" },
    ],
  },
};

// Mock recent chats for sidebar
export const MOCK_RECENT_CHATS = [
  {
    _id: "chat-001",
    title: "Compliance Review for Active probiotics supp...",
    workflowType: "compliance_review" as const,
    updatedAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
  },
  {
    _id: "chat-002",
    title: "2184369 - Cross-Brand - Celeb Retailer Jan He...",
    workflowType: "artwork_review" as const,
    updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  },
  {
    _id: "chat-003",
    title: "Claim Generation - Positioning for...",
    workflowType: "claim_generation" as const,
    updatedAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
  },
  {
    _id: "chat-004",
    title: "Brief Generation - Strategy - Positioning for",
    workflowType: "brief_creation" as const,
    updatedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    _id: "chat-005",
    title: "Compliance Review - Dairy Health Claims UK",
    workflowType: "compliance_review" as const,
    updatedAt: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
  },
];

// Workflow type display names
export const WORKFLOW_TYPE_LABELS: Record<string, string> = {
  compliance_review: "Compliance Review",
  compare: "Compare",
  review: "Review",
  artwork_review: "Artwork Review", // backward compat for old chats
  brief_creation: "Brief Creation",
  claim_generation: "Claim Generation",
  project: "Project",
  general: "Chat",
};

// Initial prompt messages per workflow type
export const WORKFLOW_INITIAL_PROMPTS: Record<string, string> = {
  compliance_review:
    "Let's get started with your Compliance Review. What claim do you want to verify?",
  compare:
    "Let's compare your files. Upload the reference document and the deliverable you'd like reviewed.",
  review:
    "Upload the file(s) you'd like reviewed and I'll create a review ticket for expert feedback.",
  artwork_review:
    "Let's review your artwork for compliance. What asset would you like me to check?",
  brief_creation:
    "I'll help you create a compliant creative brief. What's the campaign or product?",
  claim_generation:
    "I'll generate compliant claim options for you. What product or benefit area should I focus on?",
  project: "I want to set up a new project",
  general: "How can I help you today?",
};
